import React from 'react';
import { AlertTriangle, Package, Calendar, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DuplicateCheckResponse } from '../api/payment';

interface DuplicateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateInfo: DuplicateCheckResponse;
  onProceedAnyway: () => void;
  loading?: boolean;
}

const DuplicateOrderModal: React.FC<DuplicateOrderModalProps> = ({
  isOpen,
  onClose,
  duplicateInfo,
  onProceedAnyway,
  loading = false,
}) => {
  const navigate = useNavigate();

  if (!isOpen || !duplicateInfo.hasDuplicate) return null;

  const handleViewExistingOrder = () => {
    onClose();
    if (duplicateInfo.existingOrderId) {
      navigate(`/order/${duplicateInfo.existingOrderId}`);
    } else {
      navigate('/profile');
    }
  };

  const formattedDate = duplicateInfo.existingOrderDate
    ? new Date(duplicateInfo.existingOrderDate).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative z-10 overflow-hidden border border-amber-100 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center justify-between">
          <div className="flex items-center space-x-2.5 text-amber-800">
            <div className="p-1.5 bg-amber-200/60 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <h3 className="font-bold text-base sm:text-lg">Duplicate Order Notice</h3>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 text-amber-700 hover:text-amber-900 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            You already have an active order currently in process for item(s) in your purchase:
          </p>

          {/* Duplicate Details Card */}
          <div className="bg-amber-50/50 border border-amber-200/70 rounded-xl p-4 space-y-3 text-xs sm:text-sm">
            {duplicateInfo.duplicateItems?.map((item, index) => (
              <div key={index} className="flex items-start space-x-2.5">
                <Package className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="font-semibold text-gray-900 line-clamp-2">{item.title}</span>
              </div>
            ))}

            <div className="border-t border-amber-200/50 pt-2.5 flex items-center justify-between text-gray-500 text-xs">
              <span className="flex items-center">
                <Calendar className="w-3.5 h-3.5 mr-1 text-amber-600" />
                Ordered: {formattedDate || 'Recently'}
              </span>
              <span className="font-mono text-gray-700">
                #{duplicateInfo.existingOrderDisplayId || duplicateInfo.existingOrderId}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Would you like to view your active order or proceed with placing another order?
          </p>

          {/* Actions */}
          <div className="space-y-2.5 pt-2">
            <button
              onClick={handleViewExistingOrder}
              disabled={loading}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 px-4 rounded-xl font-bold transition-all shadow-md flex items-center justify-center text-sm"
            >
              <span>View Active Order</span>
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </button>

            <button
              onClick={onProceedAnyway}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 py-2.5 px-4 rounded-xl font-semibold transition-all text-xs sm:text-sm"
            >
              {loading ? 'Processing Order...' : 'Proceed & Place Order Anyway'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateOrderModal;
